/*jshint esversion: 11 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import { debounce } from 'lodash';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageDataLoader from '../Utility/PageDataLoader';
import Paginator from '../Utility/Paginator';
import WeeklyLogDetailsView from '../../components/WeeklyLogDetailsView';
import moment from 'moment';
import { getWeeklyActionRecordList } from '../../api/weeklyActionRecord';
import { getOfferingList } from '../../api/offering';
import { getBatchList } from '../../api/batch';
import { validatePermissionKey } from '../../helpers/permission';
import { exportWeeklyLogPdf } from '../../helpers/exportWeeklyLogPdf';
import { VIEW_WEEKLY_ACTION_RECORD_LIST } from '../Utility/PermissionKeys';

const FILTER_SEARCH_LIMIT = 50;

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '38px'
  })
};

const WeeklyActionRecordList = ({ authUser }) => {
  const [breadcrumbItems] = useState([
    { title: 'Weekly Logs', link: '#' },
    { title: 'All Weekly Logs', link: '#' }
  ]);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageLimit: 10,
    totalEntries: 0,
    totalPages: 0
  });
  const [offeringSelectOptions, setOfferingSelectOptions] = useState([]);
  const [batchSelectOptions, setBatchSelectOptions] = useState([]);
  const [offeringSelectLoading, setOfferingSelectLoading] = useState(false);
  const [batchSelectLoading, setBatchSelectLoading] = useState(false);
  const [selectedOffering, setSelectedOffering] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [exportRecord, setExportRecord] = useState(null);
  const [exportingRecordId, setExportingRecordId] = useState(null);
  const [exportError, setExportError] = useState('');
  const exportContainerRef = useRef(null);

  const buildListFilters = (overrides = {}) => ({
    offering_id:
      overrides.offering_id !== undefined
        ? overrides.offering_id
        : selectedOffering?.value || undefined,
    batch_id:
      overrides.batch_id !== undefined
        ? overrides.batch_id
        : selectedBatch?.value || undefined,
    filter_start_date:
      overrides.filter_start_date !== undefined
        ? overrides.filter_start_date
        : filterStartDate || undefined,
    filter_end_date:
      overrides.filter_end_date !== undefined
        ? overrides.filter_end_date
        : filterEndDate || undefined
  });

  const fetchRecords = async (
    page = pagination.currentPage,
    limit = pagination.pageLimit,
    filterOverrides = {}
  ) => {
    setLoading(true);
    setError('');
    try {
      const response = await getWeeklyActionRecordList({
        page,
        limit,
        ...buildListFilters(filterOverrides)
      });
      setRecords(response?.data || []);
      setPagination({
        currentPage: response?.metadata?.page || page,
        pageLimit: response?.metadata?.limit || limit,
        totalEntries: response?.metadata?.total_results || 0,
        totalPages: response?.metadata?.total_pages || 0
      });
    } catch (e) {
      setError('Failed to load weekly logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferingOptions = async (searchParam = '') => {
    setOfferingSelectLoading(true);
    try {
      const response = await getOfferingList(1, FILTER_SEARCH_LIMIT, {
        name: searchParam
      });
      setOfferingSelectOptions(
        (response?.data || []).map((offering) => ({
          value: offering._id,
          label: offering.name
        }))
      );
    } catch {
      setOfferingSelectOptions([]);
    } finally {
      setOfferingSelectLoading(false);
    }
  };

  const fetchBatchOptions = async (searchParam = '', offeringId = null) => {
    setBatchSelectLoading(true);
    try {
      const response = await getBatchList(1, FILTER_SEARCH_LIMIT, {
        name: searchParam,
        offering: offeringId || selectedOffering?.value || ''
      });
      setBatchSelectOptions(
        (response?.data || []).map((batch) => ({
          value: batch._id,
          label: batch.name
        }))
      );
    } catch {
      setBatchSelectOptions([]);
    } finally {
      setBatchSelectLoading(false);
    }
  };

  const searchOfferings = useCallback(
    debounce((inputValue) => {
      fetchOfferingOptions(inputValue);
    }, 300),
    []
  );

  const searchBatches = useCallback(
    debounce((inputValue, offeringId) => {
      fetchBatchOptions(inputValue, offeringId);
    }, 300),
    [selectedOffering]
  );

  useEffect(() => {
    fetchRecords(1, 10);
  }, []);

  const openDetailsModal = (record) => {
    setSelectedRecord(record);
    setDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApplyFilters = () => {
    fetchRecords(1, pagination.pageLimit);
  };

  const handleResetFilters = () => {
    setSelectedOffering(null);
    setSelectedBatch(null);
    setOfferingSelectOptions([]);
    setBatchSelectOptions([]);
    setFilterStartDate('');
    setFilterEndDate('');
    fetchRecords(1, pagination.pageLimit, {
      offering_id: undefined,
      batch_id: undefined,
      filter_start_date: undefined,
      filter_end_date: undefined
    });
  };

  const handleExportPdf = async (record) => {
    if (!record) {
      return;
    }

    const recordKey = record._id || `${record.batch_id}-${record.start_date}`;
    setExportError('');
    setExportingRecordId(recordKey);
    setExportRecord(record);

    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      await exportWeeklyLogPdf(exportContainerRef.current, record);
    } catch (e) {
      setExportError('Failed to export PDF. Please try again.');
    } finally {
      setExportingRecordId(null);
      setExportRecord(null);
    }
  };

  const isExporting = (record) => {
    const recordKey = record._id || `${record.batch_id}-${record.start_date}`;
    return exportingRecordId === recordKey;
  };

  if (
    authUser &&
    authUser.user_type === 'staff' &&
    !validatePermissionKey(authUser, VIEW_WEEKLY_ACTION_RECORD_LIST)
  ) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Weekly Logs" breadcrumbItems={breadcrumbItems} />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label>Offering</Label>
                        <Select
                          options={offeringSelectOptions}
                          value={selectedOffering}
                          onChange={(option) => {
                            setSelectedOffering(option);
                            setSelectedBatch(null);
                            setBatchSelectOptions([]);
                          }}
                          onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change') {
                              searchOfferings(inputValue);
                            }
                          }}
                          onMenuOpen={() => {
                            if (!offeringSelectOptions.length) {
                              fetchOfferingOptions('');
                            }
                          }}
                          isLoading={offeringSelectLoading}
                          isClearable
                          isSearchable
                          placeholder="Type to search offering..."
                          filterOption={() => true}
                          styles={selectStyles}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Batch</Label>
                        <Select
                          options={batchSelectOptions}
                          value={selectedBatch}
                          onChange={setSelectedBatch}
                          onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change') {
                              searchBatches(inputValue, selectedOffering?.value);
                            }
                          }}
                          onMenuOpen={() => {
                            fetchBatchOptions('', selectedOffering?.value);
                          }}
                          isLoading={batchSelectLoading}
                          isClearable
                          isSearchable
                          placeholder="Type to search batch..."
                          filterOption={() => true}
                          styles={selectStyles}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label>From Date</Label>
                        <Input
                          type="date"
                          value={filterStartDate}
                          onChange={(e) => setFilterStartDate(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label>To Date</Label>
                        <Input
                          type="date"
                          value={filterEndDate}
                          onChange={(e) => setFilterEndDate(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <div className="d-flex">
                        <Button
                          size="sm"
                          color="primary"
                          className="mr-2"
                          onClick={handleApplyFilters}
                          disabled={loading}
                        >
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          color="light"
                          className="mr-2"
                          onClick={handleResetFilters}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          color="secondary"
                          onClick={() =>
                            fetchRecords(pagination.currentPage, pagination.pageLimit)
                          }
                          disabled={loading}
                        >
                          Refresh
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {exportError && (
                    <Alert color="danger" className="mb-3">
                      {exportError}
                    </Alert>
                  )}

                  {error && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  {loading ? (
                    <div className="text-center">
                      <PageDataLoader />
                    </div>
                  ) : (
                    <div className="table-rep-plugin">
                      <div className="table-responsive mb-0" data-pattern="priority-columns">
                        <Table striped bordered responsive>
                          <thead>
                            <tr>
                              <th>SL. No</th>
                              <th>Offering</th>
                              <th>Batch</th>
                              <th>Week Date</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {records.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="text-center">
                                  No weekly logs found
                                </td>
                              </tr>
                            ) : (
                              records.map((record, index) => (
                                <tr key={record._id || index}>
                                  <th>{index + 1}</th>
                                  <td>{record.offering?.name || 'N/A'}</td>
                                  <td>{record.batch?.name || 'N/A'}</td>
                                  <td>
                                    {record.start_date
                                      ? `${moment(record.start_date).format('DD MMM YYYY')} - ${moment(
                                          record.end_date
                                        ).format('DD MMM YYYY')}`
                                      : 'N/A'}
                                  </td>
                                  <td>
                                    <Button
                                      size="sm"
                                      color="primary"
                                      className="mr-2"
                                      onClick={() => openDetailsModal(record)}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="success"
                                      onClick={() => handleExportPdf(record)}
                                      disabled={!!exportingRecordId}
                                    >
                                      {isExporting(record) ? (
                                        <>
                                          <Spinner size="sm" className="mr-1" />
                                          Exporting...
                                        </>
                                      ) : (
                                        'Export PDF'
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {!loading && (
                    <Row className="mt-2">
                      <Col md="5">
                        <p>
                          <strong>
                            Showing{' '}
                            {pagination.totalEntries === 0
                              ? 0
                              : (pagination.currentPage - 1) * pagination.pageLimit + 1}{' '}
                            to{' '}
                            {Math.min(
                              pagination.currentPage * pagination.pageLimit,
                              pagination.totalEntries
                            )}{' '}
                            of {pagination.totalEntries} entries
                          </strong>
                        </p>
                      </Col>
                      {pagination.totalPages > 0 && (
                        <Col md="7">
                          <Paginator
                            totalPages={pagination.totalPages}
                            currentPage={pagination.currentPage}
                            changePage={(page) =>
                              fetchRecords(page, pagination.pageLimit)
                            }
                          />
                        </Col>
                      )}
                    </Row>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={detailsModalOpen} toggle={closeDetailsModal} size="xl">
        <ModalHeader toggle={closeDetailsModal}>
          Weekly Log Details
          {selectedRecord?.batch?.name ? ` - ${selectedRecord.batch.name}` : ''}
        </ModalHeader>
        <ModalBody>
          {selectedRecord && <WeeklyLogDetailsView record={selectedRecord} />}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeDetailsModal}>
            Close
          </Button>
          <Button
            color="success"
            onClick={() => handleExportPdf(selectedRecord)}
            disabled={!selectedRecord || !!exportingRecordId}
          >
            {selectedRecord && isExporting(selectedRecord) ? (
              <>
                <Spinner size="sm" className="mr-1" />
                Exporting...
              </>
            ) : (
              'Export PDF'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <div
        ref={exportContainerRef}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '1100px',
          zIndex: -1
        }}
        aria-hidden="true"
      >
        {exportRecord && (
          <WeeklyLogDetailsView record={exportRecord} forExport />
        )}
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.Login?.user
});

export default connect(mapStateToProps)(WeeklyActionRecordList);
